import React, { useState, useEffect } from 'react';
import ReactECharts from 'echarts-for-react';
import { Text, Flex } from '@chakra-ui/react';
import ChartCard from './ChartCard';
import { metricsService, DealSentimentSummary } from '../../services/metricsService';
import { CHART_COLORS } from '../../utils/chartColors';

interface ConversionProbabilityChartProps {
  businessIdentifier?: string;
  timeRange?: string;
}

const FONT_FAMILY = 'TT Interphases Pro, Inter, sans-serif';

const ConversionProbabilityChart: React.FC<ConversionProbabilityChartProps> = ({
  businessIdentifier,
  timeRange = '7d',
}) => {
  const [data, setData] = useState<DealSentimentSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const summary = await metricsService.getDealSentimentSummary(businessIdentifier, timeRange);
        setData(summary);
      } catch (err: any) {
        console.error('[ConversionProbabilityChart] Error fetching data:', err);
        setError(err.message || 'Failed to load conversion probability data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [businessIdentifier, timeRange]);

  const getChartOption = () => {
    if (!data) return {};

    const categories = ['Positive', 'Neutral', 'Negative'];
    const values = [
      data.positive_sentiment_count,
      data.neutral_sentiment_count,
      data.negative_sentiment_count,
    ];
    const colors = [CHART_COLORS.positive, CHART_COLORS.neutral, CHART_COLORS.negative];

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: { fontFamily: FONT_FAMILY },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 12,
          color: '#666',
        },
        axisLine: { lineStyle: { color: '#E2E8F0' } },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          fontFamily: FONT_FAMILY,
          fontSize: 11,
          color: '#999',
        },
        splitLine: { lineStyle: { color: '#EDF2F7' } },
      },
      series: [
        {
          type: 'bar',
          data: values.map((val, idx) => ({
            value: val,
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: colors[idx] },
                  { offset: 1, color: colors[idx] + '99' },
                ],
              },
              borderRadius: [4, 4, 0, 0],
            },
          })),
          barWidth: '40%',
          label: {
            show: true,
            position: 'top',
            fontFamily: FONT_FAMILY,
            fontSize: 12,
            fontWeight: 'bold',
          },
        },
      ],
    };
  };

  const isEmpty = data &&
    data.positive_sentiment_count === 0 &&
    data.neutral_sentiment_count === 0 &&
    data.negative_sentiment_count === 0;

  return (
    <ChartCard
      title="Conversion Probability"
      subtitle="Deal sentiment distribution across conversations"
      isLoading={isLoading}
      error={error}
    >
      {isEmpty ? (
        <Flex justify="center" align="center" minH="200px">
          <Text color="gray.400" fontSize="sm">No data available</Text>
        </Flex>
      ) : (
        <ReactECharts
          option={getChartOption()}
          style={{ height: '300px', width: '100%' }}
          opts={{ renderer: 'canvas' }}
        />
      )}
    </ChartCard>
  );
};

export default ConversionProbabilityChart;
